import { useNavigate } from 'react-router-dom';
import { isCurrencyCode } from 'pi-kiosk-shared';
import { PickupQrCode } from '../../orders/components/PickupQrCode.js';
import { InformationalPickupAckButton } from '../../orders/components/InformationalPickupAckButton.js';
import { toMajorUnits } from '../../shop/cart/money.js';
import { toCustomerFacingErrorMessage } from '../../../shared/errors/toCustomerFacingErrorMessage.js';
import { useCustomerFormatters } from '../../../shared/hooks/useCustomerFormatters.js';
import { hasCustomerApiTenant } from '../../../shared/tenant/customerApiTenant.js';
import { Button } from '../../../shared/ui/Button.js';
import { ScreenState } from '../../../shared/ui/ScreenState.js';
import type { PaymentHistoryItemDTO } from '../accountTypes.js';
import { formatCollectTimingLabel } from '../hooks/useAccountOrdersScreen.js';
import { useAccountOrderDetailScreen } from '../hooks/useAccountOrderDetailScreen.js';
import { useOrderReceiptRow } from '../hooks/useOrderReceiptRow.js';
import {
  buildOrderDetailApiSubtitleParts,
  buildOrderSubtitleSearchParts,
  type OrderSubtitleScreen,
} from '../ordersDetailPresentation.js';
import {
  resolveFulfillmentStatusLabel,
  resolvePickupScheduleTypeLabel,
} from '../presenters/orderStatusPresentation.js';
import {
  collectionModeLabelKey,
  resolveCanBuyAgain,
  type UnifiedOrderCard,
} from '../presenters/unifiedOrdersPresenter.js';
import { resolveBuyAgainError } from '../resolveBuyAgainError.js';
import { AccountOrderActionSection } from './AccountOrderActionSection.js';
import { AccountOrderRefundsPanel } from './AccountOrderRefundsPanel.js';
import { AccountReceiptDownloadButton } from './AccountReceiptDownloadButton.js';
import { FulfillmentDecisionPanel } from './FulfillmentDecisionPanel.js';
import { FulfillmentCancelPanel } from './FulfillmentCancelPanel.js';
import { OrderLiveNoticesPanel } from './OrderLiveNoticesPanel.js';
import { SealedShippingPolicyPanel } from './SealedShippingPolicyPanel.js';
import { ShippingOrderActionsPanel } from './ShippingOrderActionsPanel.js';
import {
  isCustomerCancelAllowedStatus,
  useOrderLiveStatus,
} from '../hooks/useOrderLiveStatus.js';

export interface AccountOrderDetailContentProps {
  readonly transactionId: string;
  /** List card from Objednávky when found — drives subtitle context block. */
  readonly listCard?: UnifiedOrderCard | null;
  /** Receipt row from unified list card — avoids payments pagination miss. */
  readonly listReceiptRow?: PaymentHistoryItemDTO | null;
}

export function AccountOrderDetailContent({
  transactionId,
  listCard,
  listReceiptRow,
}: AccountOrderDetailContentProps): JSX.Element {
  const navigate = useNavigate();
  const { formatPrice, formatDateTime: formatDate } = useCustomerFormatters();
  const screen = useAccountOrderDetailScreen(transactionId);
  // G2 — preserve undefined (card miss → fetch). Do not collapse via `?? null`.
  let effectiveListReceiptRow: PaymentHistoryItemDTO | null | undefined = listReceiptRow;
  if (effectiveListReceiptRow === undefined && listCard != null) {
    effectiveListReceiptRow = listCard.receipt;
  }
  const detailPaymentId =
    screen.data != null && screen.data.paymentId !== null ? screen.data.paymentId : null;
  const receiptLookupTxId =
    screen.data != null ? screen.data.transactionId : transactionId;
  const receiptRow = useOrderReceiptRow(
    detailPaymentId,
    effectiveListReceiptRow,
    receiptLookupTxId,
  );
  const orderIdFromParam = Number(transactionId);
  const live = useOrderLiveStatus(
    Number.isFinite(orderIdFromParam) && orderIdFromParam > 0 ? orderIdFromParam : null,
  );

  if (screen.phase === 'invalid') {
    return <ScreenState variant="empty" title={screen.t('account.orders.invalid')} />;
  }
  if (screen.phase === 'loading') {
    return <ScreenState variant="loading" message={screen.t('account.orders.detailLoading')} />;
  }
  if (screen.phase === 'error') {
    return <ScreenState variant="error" message={screen.errorMessage} />;
  }
  if (screen.phase === 'empty' || !screen.data) {
    return <ScreenState variant="empty" title={screen.t('account.orders.notFound')} />;
  }

  const data = screen.data;
  const shopCandidateForBuyAgain =
    data.tenantCode !== undefined && data.tenantCode.trim().length > 0
      ? data.tenantCode.trim()
      : screen.rowTenantCode.trim();
  const usableShop =
    hasCustomerApiTenant(shopCandidateForBuyAgain) ||
    hasCustomerApiTenant(screen.tenantCode);
  const canBuyAgain = resolveCanBuyAgain({
    usableShop,
    paymentStatus: data.paymentStatus,
    paymentRequired: data.paymentRequired,
    buyAgainEligible: data.buyAgainEligible,
    currency: data.currency,
  });

  // G1 — mirror list card (`card.receipt !== null`): show when list or fetched row exists.
  // AccountReceiptDownloadButton owns status chips.
  const panelReceiptRow = effectiveListReceiptRow ?? receiptRow;
  const showReceiptPanel = panelReceiptRow != null;
  const showActionPanels = canBuyAgain || showReceiptPanel;

  const subtitleScreen: OrderSubtitleScreen = {
    t: screen.t,
    formatCollectTiming: (timing) => formatCollectTimingLabel(screen.t, timing),
    formatCollectionMode: (mode) => {
      const key = collectionModeLabelKey(mode);
      return key === null ? null : screen.t(key);
    },
  };
  const subtitleParts =
    listCard != null
      ? buildOrderSubtitleSearchParts(listCard, subtitleScreen, formatDate)
      : buildOrderDetailApiSubtitleParts(data, subtitleScreen, formatDate);
  const subtitleTestId =
    listCard != null
      ? `customer-order-detail-list-subtitle-${data.transactionId}`
      : `customer-order-detail-api-subtitle-${data.transactionId}`;
  /** G3 — API subtitle already carries pickup schedule / promised time; avoid duplicate body lines. */
  const showListParityBodyLines = listCard != null;

  // G15 — primary status prefers live.snapshot.status (A12 authoritative); fallback fetch.
  const primaryStatus =
    live.snapshot != null && live.snapshot.status.length > 0
      ? live.snapshot.status
      : data.status;
  const liveStatusLabel = resolveFulfillmentStatusLabel(screen.t, primaryStatus);

  let cancelFulfillmentId = 0;
  if (live.snapshot != null && live.snapshot.fulfillmentId > 0) {
    cancelFulfillmentId = live.snapshot.fulfillmentId;
  } else if (typeof data.fulfillmentId === 'number' && data.fulfillmentId > 0) {
    cancelFulfillmentId = data.fulfillmentId;
  }
  const cancelStatus =
    live.snapshot?.status ?? (typeof data.status === 'string' ? data.status : null);
  const showA10Cancel =
    cancelFulfillmentId > 0 &&
    live.snapshot?.pendingUnavailable == null &&
    isCustomerCancelAllowedStatus(cancelStatus);

  // G5 — ShippingOrderActionsPanel detail fallback when live.snapshot missing (parity cancel).
  let shippingFulfillmentId = 0;
  if (live.snapshot != null && live.snapshot.fulfillmentId > 0) {
    shippingFulfillmentId = live.snapshot.fulfillmentId;
  } else if (typeof data.fulfillmentId === 'number' && data.fulfillmentId > 0) {
    shippingFulfillmentId = data.fulfillmentId;
  }

  return (
    <div className="flex flex-col gap-4" data-testid={`customer-order-detail-${data.transactionId}`}>
      {subtitleParts.length > 0 ? (
        <p
          className="text-sm text-[var(--color-on-surface-muted)]"
          data-testid={subtitleTestId}
        >
          {subtitleParts.join(' · ')}
        </p>
      ) : null}
      {live.snapshot?.pendingUnavailable != null ? (
        <FulfillmentDecisionPanel
          pending={live.snapshot.pendingUnavailable}
          live={live}
          statusLabel={liveStatusLabel}
        />
      ) : null}
      {showA10Cancel ? (
        <FulfillmentCancelPanel
          fulfillmentId={cancelFulfillmentId}
          live={live}
          statusLabel={liveStatusLabel}
        />
      ) : null}
      <OrderLiveNoticesPanel
        delayNotice={live.snapshot?.delayNotice ?? null}
        opsNotice={live.snapshot?.opsNotice ?? null}
        noShowNotice={live.snapshot?.noShowNotice ?? null}
        formatDate={formatDate}
      />
      <p
        className="text-sm text-[var(--color-on-surface)]"
        data-testid={`customer-order-detail-status-${data.transactionId}`}
      >
        {screen.t('account.orders.itemStatus', {
          status: liveStatusLabel,
        })}
      </p>
      {data.amountMinor != null ? (
        <p
          className="text-sm font-medium text-[var(--color-on-surface)]"
          data-testid={`customer-order-detail-amount-${data.transactionId}`}
        >
          {formatPrice(toMajorUnits(data.amountMinor), data.currency ?? undefined)}
        </p>
      ) : null}
      <AccountOrderRefundsPanel
        transactionId={data.transactionId}
        refunds={data.refunds}
        refundStatus={data.refundStatus}
        t={screen.t}
      />
      {showListParityBodyLines ? (
        <>
          <p className="text-sm text-[var(--color-on-surface-muted)]">
            {screen.t('account.orders.pickupType', {
              type: resolvePickupScheduleTypeLabel(screen.t, data.pickupScheduleType),
            })}
          </p>
          {data.promisedPickupAt ? (
            <p className="text-sm text-[var(--color-on-surface-muted)]">
              {screen.t('account.orders.scheduledAt', {
                at: formatDate(data.promisedPickupAt),
              })}
            </p>
          ) : null}
        </>
      ) : null}
      {data.paymentRequired ? (
        <div className="flex flex-col gap-2 rounded-md border border-[var(--color-border)] p-3">
          <p className="text-sm text-[var(--color-danger)]">{screen.t('account.orders.paymentRequired')}</p>
          <Button
            type="button"
            intent="primary"
            size="md"
            block
            onClick={() => {
              void navigate(screen.paymentsHref);
            }}
          >
            {screen.t('account.orders.goToPayment')}
          </Button>
        </div>
      ) : (
        <Button
          type="button"
          intent="secondary"
          size="md"
          block
          data-testid={`customer-order-detail-open-durable-confirmation-${data.transactionId}`}
          onClick={() => {
            void navigate(`/account/orders/${data.transactionId}/confirmation`);
          }}
        >
          {screen.t('account.durableConfirmation.openLink')}
        </Button>
      )}
      {screen.pickupQr.data ? <PickupQrCode detail={screen.pickupQr.data} /> : null}
      {data.selfCollectVisible === true ? (
        <div className="flex flex-col gap-2 rounded-md border border-[var(--color-border)] p-3">
          <p className="text-sm font-medium text-[var(--color-on-surface)]">
            {screen.t('account.orders.selfCollectTitle')}
          </p>
          <Button
            type="button"
            intent="primary"
            size="md"
            block
            loading={screen.selfCollectMutation.isPending}
            onClick={() => {
              const rowTenant =
                data.tenantCode !== undefined && data.tenantCode.trim().length > 0
                  ? data.tenantCode.trim()
                  : screen.rowTenantCode.trim();
              screen.selfCollectMutation.mutate({
                transactionId: data.transactionId,
                version: data.version,
                ...(rowTenant.length > 0 && rowTenant !== '__platform__'
                  ? { tenantCode: rowTenant }
                  : {}),
              });
            }}
          >
            {screen.t('account.orders.selfCollect')}
          </Button>
          {screen.selfCollectMutation.isError ? (
            <p className="text-xs text-[var(--color-danger)]">
              {toCustomerFacingErrorMessage(screen.selfCollectMutation.error, {
                fallback: screen.t('errors.generic'),
              })}
            </p>
          ) : null}
        </div>
      ) : null}
      {data.informationalPickupAckVisible === true ? (
        <InformationalPickupAckButton transactionId={data.transactionId} version={data.version} />
      ) : null}
      <SealedShippingPolicyPanel
        fulfillmentMode={
          live.snapshot?.fulfillmentMode ?? data.fulfillmentMode ?? null
        }
        shippingMethodSnapshot={
          live.snapshot?.shippingMethodSnapshot ?? data.shippingMethodSnapshot
        }
        promisedDeliveryByAt={
          live.snapshot?.promisedDeliveryByAt ??
          data.promisedDeliveryByAt ??
          data.shipment?.promisedDeliveryByAt ??
          null
        }
        autoDeliverEligibleAt={
          live.snapshot?.autoDeliverEligibleAt ??
          data.autoDeliverEligibleAt ??
          data.shipment?.autoDeliverEligibleAt ??
          null
        }
        tenantHandoffKind={
          live.snapshot?.tenantHandoffKind ??
          data.tenantHandoffKind ??
          data.shipment?.tenantHandoffKind ??
          null
        }
        status={primaryStatus}
        formatDate={formatDate}
      />
      {shippingFulfillmentId > 0 ? (
        <ShippingOrderActionsPanel
          transactionId={data.transactionId}
          fulfillmentId={shippingFulfillmentId}
          fulfillmentMode={live.snapshot?.fulfillmentMode ?? data.fulfillmentMode}
          status={live.snapshot?.status ?? data.status}
          version={live.snapshot?.version ?? data.version}
          shippingMethodSnapshot={
            live.snapshot?.shippingMethodSnapshot ?? data.shippingMethodSnapshot
          }
          promisedDeliveryByAt={
            live.snapshot?.promisedDeliveryByAt ??
            data.promisedDeliveryByAt ??
            data.shipment?.promisedDeliveryByAt ??
            null
          }
          autoDeliverEligibleAt={
            live.snapshot?.autoDeliverEligibleAt ??
            data.autoDeliverEligibleAt ??
            data.shipment?.autoDeliverEligibleAt ??
            null
          }
          lateComplaintFiledAt={
            live.snapshot?.lateComplaintFiledAt ??
            data.lateComplaintFiledAt ??
            data.shipment?.lateComplaintFiledAt ??
            null
          }
          lateComplaintEverFiled={
            live.snapshot?.lateComplaintEverFiled === true ||
            data.lateComplaintEverFiled === true
          }
          tenantHandoffKind={
            live.snapshot?.tenantHandoffKind ??
            data.tenantHandoffKind ??
            data.shipment?.tenantHandoffKind ??
            null
          }
          onAckSuccess={() => {
            void live.reconstruct();
          }}
          onReportSuccess={() => {
            void live.reconstruct();
          }}
        />
      ) : null}

      {showActionPanels ? (
        <div
          className="flex flex-col gap-3 md:flex-row md:items-stretch"
          data-testid="customer-order-detail-action-panels"
        >
          {canBuyAgain ? (
            <AccountOrderActionSection
              title={screen.t('account.orders.buyAgainTitle')}
              testId="customer-order-detail-buy-again-panel"
            >
              <Button
                type="button"
                intent="secondary"
                size="md"
                block
                loading={screen.buyAgain.isPending}
                onClick={() => {
                  void screen.buyAgain.startBuyAgain({
                    kind: 'order',
                    transactionId: data.transactionId,
                    ...(isCurrencyCode(data.currency) ? { currency: data.currency } : {}),
                    ...(hasCustomerApiTenant(shopCandidateForBuyAgain)
                      ? { shopTenantCode: shopCandidateForBuyAgain }
                      : {}),
                  });
                }}
              >
                {screen.t('account.orders.buyAgain')}
              </Button>
              {screen.buyAgain.error ? (
                <p className="text-xs text-[var(--color-danger)]">
                  {
                    resolveBuyAgainError(
                      screen.buyAgain.error,
                      screen.t('account.orders.buyAgainFailed'),
                    ).message
                  }
                </p>
              ) : null}
            </AccountOrderActionSection>
          ) : null}

          {showReceiptPanel && panelReceiptRow !== null ? (
            <AccountOrderActionSection
              title={screen.t('receipt.title')}
              testId="customer-order-detail-receipt-panel"
            >
              <AccountReceiptDownloadButton
                row={panelReceiptRow}
                block
                screenName="account_orders"
              />
            </AccountOrderActionSection>
          ) : null}
        </div>
      ) : null}

      {screen.buyAgain.trimDialog}
    </div>
  );
}
